<template>
  <div class="forgot-shell">
    <div class="forgot-card">
      <button class="back-link" type="button" @click="goBack">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="15 18 9 12 15 6"/>
        </svg>
        Voltar
      </button>

      <h1 class="forgot-title">Esqueci minha senha</h1>

      <div v-if="sent" class="success-state">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1f8a45" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
        <p class="success-text">
          Se essa conta existir e tiver um e-mail cadastrado, você receberá um link de redefinição em breve.<br/>
          <span class="success-sub">Verifique também sua caixa de spam.</span>
        </p>
        <button class="btn-primary" type="button" @click="goBack">Voltar para o login</button>
      </div>

      <form v-else class="forgot-form" @submit.prevent="handleSubmit">
        <p class="forgot-desc">
          Informe o identificador da obra e seu usuário. Se houver um e-mail cadastrado, enviaremos um link de redefinição.
        </p>

        <div class="input-field">
          <label for="fg-system">Obra (identificador)</label>
          <input
            id="fg-system"
            v-model="systemUsername"
            type="text"
            placeholder="Ex.: minhaobra"
            autocomplete="off"
          />
        </div>

        <div class="input-field">
          <label for="fg-username">Seu usuário</label>
          <input
            id="fg-username"
            v-model="username"
            type="text"
            placeholder="Ex.: joao.silva"
            autocomplete="username"
          />
        </div>

        <p v-if="error" class="error-text">{{ error }}</p>

        <button class="btn-primary" type="submit" :disabled="loading">
          {{ loading ? 'Enviando...' : 'Enviar link de redefinição' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '@/store/api'

export default defineComponent({
  name: 'ForgotPasswordView',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const systemUsername = ref('')
    const username = ref('')
    const error = ref('')
    const loading = ref(false)
    const sent = ref(false)

    onMounted(() => {
      const sys = String(route.query.system || '').trim()
      if (sys) systemUsername.value = sys
    })

    const handleSubmit = async () => {
      error.value = ''
      if (!systemUsername.value.trim() || !username.value.trim()) {
        error.value = 'Preencha o identificador da obra e seu usuário.'
        return
      }
      loading.value = true
      try {
        await apiFetch('/forgot-password', {
          method: 'POST',
          body: JSON.stringify({
            username: username.value.trim(),
            systemUsername: systemUsername.value.trim()
          })
        }, false)
        sent.value = true
      } catch {
        // Always show success to not reveal username existence
        sent.value = true
      } finally {
        loading.value = false
      }
    }

    const goBack = () => {
      const sys = systemUsername.value.trim()
      if (sys) {
        router.push(`/login/${encodeURIComponent(sys)}`)
      } else {
        router.push('/')
      }
    }

    return { systemUsername, username, error, loading, sent, handleSubmit, goBack }
  }
})
</script>

<style scoped lang="scss">
.forgot-shell {
  min-height: 100vh;
  min-height: 100dvh;
  display: grid;
  place-items: center;
  background: #f5f2ee;
  padding: 24px;
}

.forgot-card {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(31, 30, 26, 0.1);
  padding: 36px 36px 40px;
  display: grid;
  gap: 20px;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #888;
  font-size: 13px;
  padding: 0;
  width: fit-content;
  transition: color 0.15s;

  &:hover { color: #1f1e1a; }
}

.forgot-title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #1f1e1a;
  line-height: 1.2;
}

.forgot-desc {
  margin: 0;
  font-size: 14px;
  color: #5a5752;
  line-height: 1.6;
}

.forgot-form {
  display: grid;
  gap: 16px;
}

.input-field {
  display: grid;
  gap: 6px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: #1f1e1a;
  }

  input {
    width: 100%;
    padding: 10px 12px;
    border: 1.5px solid rgba(31, 30, 26, 0.2);
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.15s;
    box-sizing: border-box;

    &:focus { border-color: #1f1e1a; }
  }
}

.error-text {
  margin: 0;
  font-size: 13px;
  color: #a54b2a;
}

.btn-primary {
  padding: 12px;
  background: #1f1e1a;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

.success-state {
  display: grid;
  gap: 16px;
  justify-items: center;
  text-align: center;
}

.success-text {
  margin: 0;
  font-size: 15px;
  color: #2c2b27;
  line-height: 1.6;
}

.success-sub {
  font-size: 13px;
  color: #888;
}

@media (max-width: 480px) {
  .forgot-card { padding: 28px 22px 32px; }
}
</style>
